import { computed, defineComponent } from "vue";
import { mapActions, mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";
import { UserManager } from "oidc-client";
import moment from "moment";
import Infrastructure from "../../common/infrastructure";

import "bootstrap-datepicker/dist/css/bootstrap-datepicker.css";

import AxxessHomeRouter from "../AxxessHome/AxxessHomeRouter";
import AxxessHelpRouter from "../AxxessHelp/AxxessHelpRouter";
import { AccessService } from "../../services/access.service";
import AuthService from "../../services/auth.service";
import { HttpService } from "../../services/http.service";
import { RolesService } from "../../services/roles.service";
import { AgencyService, IGetOrganization } from "../../services/agency.service";
import { MessageService } from "../../services/message-service";
import IdleTimerService from "../../services-vue/idle-timer.service";
import { AttendanceTypeEnum } from "../../enums/enums";

import type { IRoles } from "../../resources-vue/vue-interfaces/i-roles";
import { IAgency } from "../../resources-vue/vue-interfaces/i-agency";
import { GroupedAgencies } from "../../resources-vue/vue-models/agency";
import { Role } from "../../models/role.model";
import { OidcConfiguration } from "../../common/oidc-configuration";
import { PermissionManager } from "../../common/utilities/permission-manager";
import { MarketingContent } from "../../pages/app/marketing-content";
import { AppCache } from "../../pages/app/app-cache";
import { checkPermission } from "../../common/vue-helpers/vue-router";
import eventBus from "../../utilities/eventBus";
import { jitsiRoomHandler } from "../../utilities/jitsiRoomHandler";
import { isMobileScreenSize } from "../../utilities/isMobileScreenSize";
import { useLoadingIndicator } from "../Common/LoadingIndicator/store/useLoadingIndicator";
import { AgencyEvents } from "./components/AgencyList/AgencyList";
import { useApp } from "./useApp";
import { useConversationStore } from "../MessageCenter/store/useConversationStore";
import { useCallStore } from "../MessageCenter/store/useCallStore";
import { ApplicationEnum } from "../../enums/ApplicationEnum";
import {
    ICallControls,
    ICallSignalNotification
} from "../../resources-vue/vue-interfaces/messaging-service/i-notification";

import LoadingIndicator from "../../pages-vue/Common/LoadingIndicator/LoadingIndicator.vue";
import AppNav from "./components/AppNav/AppNav.vue";
import AgencyList from "./components/AgencyList/AgencyList.vue";
import CentralNav from "./components/CentralNav/CentralNav.vue";
import EmployeeList from "../Common/EmployeeList/EmployeeList.vue";
import PatientList from "../Common/PatientList/PatientList.vue";
import WindowPopupNotification from "../Common/WindowPopupNotification/WindowPopupNotification.vue";
import Notification from "./components/Notification/Notification.vue";
import DashboardHeader from "../../resources-vue/vue-elements/DashboardHeader/DashboardHeader.vue";
import PlannerLevelTwo from "../AxxessHome/components/PlannerLevelTwo/PlannerLevelTwo.vue";
import SurveyModal from "../../resources-vue/vue-dialogs/SurveyModal/SurveyModal.vue";

const NPS_SURVEY = {
    startDate: "2026-10-21",
    endDate: "2026-11-12",
    maxReminders: 3,
    storageKey: "axxess-nps-survey-2026",
    sessionKey: "axxess-nps-survey-2026-shown",
};

interface NpsSurveyState {
    remindCount: number;
    taken: boolean;
}

export default defineComponent({
    components: {
        WindowPopupNotification,
        LoadingIndicator,
        AppNav,
        AgencyList,
        CentralNav,
        EmployeeList,
        PatientList,
        Notification,
        DashboardHeader,
        PlannerLevelTwo
    },

    data(vm) {
        return {
            _rolesService: null,
            _authService: null,
            _idleTimerService: null,
            _httpService: null,
            _marketingContent: null,
            _oidcConfiguration: null,
            _accessService: null,
            _appCache: null,
            _permissionManager: null,
            _userManager: null,
            dialogRef: null,
            _branchesService: null,
            _agencyService: null as AgencyService,
            _messageService: null as MessageService,
            router: null,
            areRolesLoaded: false,
            isLoggedIn: false,
            startApp: false,
            checkAccess: null,
            showAside: false,
            showEmployees: false,
            showPatients: false,
            groupedAgencies: new Map<string, GroupedAgencies>(),
            hasError: false,
            isLoading: true,
            isAppLoading: true,
            lisitingsCompleted: 0,
            totalListingCount: 1,
            appId: null,
            agenciesList: null,
            userOs: "",
            openAgencyNav: true as boolean,
            isHelpPage: false as boolean,
            releaseEnv: null as string,
            releaseVer: null as string,
            releaseTime: null as string
        };
    },

    async created() {
        let os = navigator.userAgent;
        if (os.search("Windows") !== -1) {
            this.userOs = "Windows";
        }
        window.sessionStorage.setItem("userOs", this.userOs);

        this._messageService = Container.instance.get(MessageService);
        this._rolesService = Container.instance.get(RolesService);
        this._authService = Container.instance.get(AuthService);
        this._httpService = Container.instance.get(HttpService);
        this._httpService.configureFetch();
        this._oidcConfiguration = Container.instance.get(OidcConfiguration);
        this._accessService = Container.instance.get(AccessService);
        this._marketingContent = Container.instance.get(MarketingContent);
        this._appCache = Container.instance.get(AppCache);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._userManager = Container.instance.get(UserManager);
        this._agencyService = Container.instance.get(AgencyService);
        this._idleTimerService = new IdleTimerService();
        this.releaseEnv = Infrastructure.releaseEnvironment;
        this.releaseVer = Infrastructure.releaseVersion;
        this.releaseTime = Infrastructure.releaseTimestamp
            ? moment(Infrastructure.releaseTimestamp).format("MM/DD/YYYY hh:mm:ss a")
            : "";
        await this.initUser();
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    watch: {
        async $route(newPath) {
            if (this.startApp) {
                if (this.checkAccess && !this.checkAccess(newPath)) {
                    this.$router.push("/help");
                }
            }
            this.$route.path.includes("/axxess-central") ? (this.showAside = false) : (this.showAside = true);
            this.$route.path.includes("/axxess-central")
                ? (this.appId = "central-app")
                : (this.appId = "central-planner");
            this.$route.path.includes("/help") ? (this.isHelpPage = true) : (this.isHelpPage = false);
            this.updateRouter();
        },

        areRolesLoaded() {
            this.areRolesLoadedChanged();
        },
        async isUserOnCall() {
            if (this.currentCallDetails.isUserOnCall) {
                await this._idleTimerService.stopIdleTimer();
            } else {
                await this._idleTimerService.resetIdleTime();
                await this._idleTimerService.timer(this.$dialog);
            }
        },
        roles() {
            this.updateRouter();
        }
    },
    computed: {
        ...mapState(useApp, [
            "showLogo",
            "organizations",
            "connectPermissionLoaded",
            "hasAxxessConnectPermission",
            "isAnyPremiumAgency",
            "isClinician",
            "roles"
        ]),
        ...mapState(useCallStore, ["currentCallDetails"]),

        showContainer() {
            return {
                "container-fluid": !this.$route.path.includes("/axxess-central")
            };
        }
    },
    async mounted() {
        if (isMobileScreenSize()) this.openAgencyNav = false;
        try {
            this.showLoading(false);
            await this._idleTimerService.timer(this.$dialog);
            await this.startMarketingContent();
        } catch (error) {
            console.error(error);
        } finally {
            this.showLoading(false);
        }

        this.areRolesLoadedChanged();

        eventBus.on("joinMeeting", async (callDetails: ICallSignalNotification) => {
            let callControls: ICallControls;
            if (callDetails.isGroupNotification) {
                callControls = {
                    audioEnabled: false,
                    videoEnabled: false
                };
            } else {
                callControls = {
                    audioEnabled: true,
                    videoEnabled: false
                };
            }
            const roomDetails = (await this._messageService.getConversationRoomStatus(callDetails.conversationId))
                .roomDetails;

            await jitsiRoomHandler(this, callDetails.roomId, roomDetails.startTime, callControls);
            await this.markAttendanceForUser(AttendanceTypeEnum.Join, callDetails.conversationId, callDetails.roomId);
        });
        eventBus.on("agencyNavOpened", async (agencyNavOpened: boolean) => {
            this.openAgencyNav = agencyNavOpened;
        });
        if (window.localStorage.getItem("logoutCallbackSync")) {
            let callbackValue = window.localStorage.getItem("logoutCallbackSync");
            window.localStorage.removeItem("logoutCallbackSync");
            if (callbackValue === "true") {
                await this._authService.logout();
            }
        }
        if (window.localStorage.getItem("axxesscentral-solution-redirect")) {
            const redirectqueryParam = window.localStorage.getItem("axxesscentral-solution-redirect");
            const agencyId = new URLSearchParams(redirectqueryParam).get("agencyId");
            if (agencyId) {
                let organization = this.organizations?.filter((x) =>
                    x.agencies.some((val) => val.id.startsWith(agencyId))
                )[0];
                if (!organization) {
                    const orgs = await this._agencyService.getOrganizations();
                    this.setOrganizations(orgs);
                    organization = this.organizations?.filter((x) =>
                        x.agencies.some((val) => val.id.startsWith(agencyId))
                    )[0];
                }
                window.localStorage.setItem("organization-id", organization.id);
                window.localStorage.setItem("organization-name", organization.name);
                window.localStorage.removeItem("axxesscentral-solution-redirect");
                this.$router.push("/axxess-central/dashboard");
            }
        }
        if (window.localStorage.getItem("reedirect")) {
            const lastUrl = window.localStorage.getItem("reedirect");
            const id = window.localStorage.getItem("reedirectId");
            const name = window.localStorage.getItem("reedirectName");
            window.localStorage.setItem("organization-id", id);
            window.localStorage.setItem("organization-name", name);
            this.$router.push(lastUrl);
            window.localStorage.removeItem("reedirect");
        }
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        ...mapActions(useApp, {
            setRoles: "SET_ROLES",
            setCookie: "setCookie",
            setUserSubId: "setUserSubId",
            setLoggedInUserDetails: "setLoggedInUserDetails",
            setAgencies: "SET_AGENCIES",
            setOrganizations: "SET_ORGANIZATIONS",
            loadConnectPermission: "loadConnectPermission",
            loadRoles: "loadRoles"
        }),
        ...mapActions(useConversationStore, ["setShowChatTutorial"]),
        ...mapActions(useCallStore, ["markAttendanceForUser"]),

        navigateToDefaultPage() {
            if (this.isClinician) {
                this.$router.replace("/clinician");
            } else if (!this.isClinician) {
                this.$router.replace("/help");
            }
        },

        updateRouter() {
            if (this.isHelpPage) {
                this.router = AxxessHelpRouter;
            } else {
                this.router = this.isClinician ? AxxessHomeRouter : AxxessHelpRouter;
            }
        },

        async initUser() {
            let isUserLoggedIn = await this._authService.isLoggedIn();
            if (!isUserLoggedIn) {
                if (window.localStorage.getItem("logoutCallbackSync")) {
                    window.localStorage.removeItem("logoutCallbackSync");
                }
                return;
            }

            await this.loadRoles();
            this.updateRouter();
            const connectPromise = this.loadConnectPermission();

            // if connect permission api takes more than 5 secs -> start loading other apis
            await Promise.race([connectPromise, this.delay(5000)]);
            await this._permissionManager.refreshUserPermissionData();
            this.isLoggedIn = true;
            this.isAppLoading = false;
            if (this.$route.path === "/") {
                if (this.connectPermissionLoaded && this.isAnyPremiumAgency && this.hasAxxessConnectPermission) {
                    // move to Axxess connect if user has permission
                    this.$router.replace("/central-messaging/clinicians/conversation");
                } else if (this.isClinician) {
                    this.$router.replace("/clinician");
                } else if (!this.isClinician) {
                    this.$router.replace("/help");
                }
            }

            if (localStorage.getItem("axxess-user-location") != null) {
                if (Object.keys(JSON.parse(localStorage.getItem("axxess-user-location"))).length) {
                    this.setCookie();
                }
            }
            this._accessService.initAccessRoles(this.roles);
            await this._appCache.restoreMissingCache();
            // Adding this property to pass to app-nav to know when roles are loaded
            this.areRolesLoaded = true;

            // this check will validate user's access to routes;
            this.checkAccess = checkPermission(this._permissionManager, await this._userManager.getUser());
            if (!this.checkAccess(this.$route)) {
                this.$router.push("/help");
            }
            const user = await this._userManager.getUser();
            this.setUserSubId(user.profile.sub);
            this.setLoggedInUserDetails({
                userId: user.profile.sub,
                loginId: user.profile.sub,
                firstName: user.profile.given_name,
                lastName: user.profile.family_name
            });
            this.startApp = true;
            this.showSurveyIfEligibile().catch(console.error)

            // prevents unhandled promise rejection
            connectPromise.catch(console.error);
        },

        isSurveyWindowOpen():boolean{
            return moment().isBetween(
                moment(NPS_SURVEY.startDate, "YYYY-MM-DD"),
                moment(NPS_SURVEY.endDate, "YYYY-MM-DD"),
                "day",
                "[]"
            );
        },
        getSurveyState(): NpsSurveyState{
            try{
                const raw = localStorage.getItem(NPS_SURVEY.storageKey);
                return raw ? JSON.parse(raw) : {remindCount: 0, taken: false}
            }
            catch{
                return {remindCount:0, taken: false}
            }
        },
        saveSurveyState(state:NpsSurveyState) {
                try{
                    localStorage.setItem(NPS_SURVEY.storageKey, JSON.stringify(state));
                }
                catch {
                    /* fail silently*/
                }
        },
        openSurveyDialog(): Promise<{wasCancelled: boolean; remindMeLater: boolean}> {
            return new Promise((resolve)=>{
                this.$dialog.open(SurveyModal, {
                    props: {
                        modal: true,
                        closeOnEscape: false,
                        dismissableMask: false,
                        showHeader: false
                    },
                    onClose: (options: any) => {
                        resolve(options?.data ?? {wasCancelled:true, remindMeLater:true});
                    }
                })
            })
        },

        async showSurveyIfEligibile() {
            if(!this.isSurveyWindowOpen()) return;
            if(localStorage.getItem(NPS_SURVEY.sessionKey)) return;

            const state = this.getSurveyState();
            if(state.taken || state.remindCount >= NPS_SURVEY.maxReminders) return;
            localStorage.setItem(NPS_SURVEY.sessionKey, "true");
            const result = await this.openSurveyDialog();
            if(result.wasCancelled) {
                state.remindCount +=1
            } else{
                state.taken = true;
            }
            this.saveSurveyState(state)
        },


        delay(ms: number) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },

        async getUserRoles() {
            try {
                let roles: Array<IRoles> = await this._rolesService.getRoles();
                return roles.map((role) => new Role(role));
            } catch (e) {
                // Roles endpoint should not be a bottle neck.
                // By returning an empty array, the user will at least see the help page.
                // Where they can select an agency.
                return [];
            }
        },

        async startMarketingContent() {
            await this._marketingContent?.start();
        },

        async areRolesLoadedChanged() {
            if (this.areRolesLoaded) {
                await this.loadAgencies();
            }
        },

        async loadAgencies(): Promise<void> {
            try {
                this.hasError = false;
                this.isLoading = true;
                this._agencyService
                    .getAgencyLisiting()
                    .then((res: IAgency[]) => {
                        this.agenciesList = res;
                        this.lisitingsCompleted++;
                        this.groupedAgencies = new Map([...this._agencyService.getAgenciesByProduct(this.roles)]);
                        if (this.totalListingCount == this.lisitingsCompleted) {
                            this.handlePromisesCompletion();
                        }
                    })
                    .catch((err: Error) => {
                        console.warn(err);
                        this.lisitingsCompleted++;
                        if (this.totalListingCount == this.lisitingsCompleted) {
                            this.handlePromisesCompletion();
                        }
                    });

                const promises = await this._agencyService.getAccountsLisiting();
                this.totalListingCount += promises.length;
                promises.forEach((singlePromise: Promise<IAgency[]>) => {
                    singlePromise
                        .then((res) => {
                            res.map((agency: IAgency) => {
                                if (agency.Application === ApplicationEnum.AxxessHospice) {
                                    let clusterDetails = sessionStorage.getItem("hospice-cluster-details")
                                        ? JSON.parse(sessionStorage.getItem("hospice-cluster-details"))
                                        : [];
                                    const found = clusterDetails.some((cluster: any) => cluster.Name === agency.Name);
                                    if (!found) clusterDetails.push(agency);
                                    sessionStorage.setItem("hospice-cluster-details", JSON.stringify(clusterDetails));
                                }
                            });
                            this.groupedAgencies = new Map([...this._agencyService.getAgenciesByProduct(this.roles)]);
                            this.lisitingsCompleted++;
                            if (this.totalListingCount == this.lisitingsCompleted) {
                                this.handlePromisesCompletion();
                            }
                        })
                        .catch((err) => {
                            console.warn(err);
                            this.lisitingsCompleted++;
                            if (this.totalListingCount == this.lisitingsCompleted) {
                                this.handlePromisesCompletion();
                            }
                        });
                });
            } catch (e) {
                console.error(e);
                this.hasError = true;
            } finally {
                this.isLoading = false;
                this.showLoading(false);
            }
        },

        async handlePromisesCompletion() {
            if (!this.groupedAgencies.size) {
                this.hasError = true;
                this.$router.push("/help");
            } else {
                this.setAgencies(this.groupedAgencies);
                this._agencyService.getProducts(null);
                this._agencyService.setAgenciesLoaded(true);
                this._agencyService.setFlags();
                eventBus.emit(AgencyEvents.Loaded);
            }
        },

        navigateToClinician() {
            if (!this.isHelpPage) this.$router.push("/clinician");
        }
    }
});
